import { Combobox } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import classNames from "classnames";
import { useField, useFormikContext } from "formik";
import { useCallback, useEffect, useState } from "react";
import Link from "next/link";

interface FormikFieldHandler {
  label: string;
  name: string;
  classes?: string;
  options: { label: string; value: string | null }[];
  linkBase?: string;
  hideLabel?: boolean;
  allowClear?: boolean;
  multiple?: boolean;
}

export const ComboBoxField = (props: FormikFieldHandler) => {
  const { label, name, classes, options, hideLabel, linkBase } = props;
  const [field, meta] = useField(props);
  const selectedValue = field.value || null;
  const [query, setQuery] = useState<string>("");
  const allowClear = props.allowClear ?? true;
  const allowMultiple: boolean = props.multiple ?? false;
  const hasNullOption = options.some((option) => option.value === null);

  const { setFieldTouched, setFieldValue } = useFormikContext();

  const onChange = useCallback(
    (value: string | null | string[]) => {
      if (allowMultiple) {
        if (value === null) {
          value = [];
        }
      }

      setFieldTouched(name, true);
      field.onChange({
        target: {
          type: "qscombobox",
          name: field.name,
          value: value,
        },
      });
    },
    [field, setFieldTouched, name, allowMultiple]
  );

  const onBlur = useCallback((name: string) => {
    setFieldTouched(name, true);
    field.onBlur({
      target: {
        name: field.name,
        value: field.value,
      },
    });
  }, []);

  let clear = useCallback(() => {
    setQuery("");
    onChange(allowMultiple ? [] : null);
  }, [setFieldValue, field.name, allowMultiple]);

  // filter the options based on the query
  let filteredOptions =
    query === ""
      ? options
      : options.filter((option) => {
          // value maybe null
          if (option.label === null) return false;
          return option.label.toLowerCase().includes(query.toLowerCase());
        });

  useEffect(() => {
    if (selectedValue) {
      if (allowMultiple && selectedValue.length > 0) {
        setQuery("");
      } else {
        setQuery("");
      }
    }
  }, [selectedValue]);

  return (
    <div className={classNames(classes, "w-full")}>
      <label
        htmlFor={name}
        className={classNames(
          hideLabel
            ? "sr-only"
            : "block text-sm font-medium leading-6 text-gray-900 w-full"
        )}
      >
        {formatLabel(label)}
      </label>
      <Combobox
        as="div"
        value={field.value}
        onChange={onChange}
        // @ts-ignore
        multiple={allowMultiple}
      >
        <div className={classNames(hideLabel ? "relative" : "relative mt-1")}>
          <div
            className={classNames(
              "bg-white block w-full rounded-md border-0 py-0 pr-1 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus-within:ring-2 focus-within:ring-inset focus-within:ring-martEye-400 focus:outline-none focus:ring-2  focus:ring-martEye-400 sm:text-sm sm:leading-6",
              selectedValue
                ? "pl-2"
                : hasNullOption && selectedValue === null
                ? "pl-2"
                : "pl-1"
            )}
          >
            <ul className="flex flex-wrap items-center gap-1 ">
              {selectedValue || (hasNullOption && selectedValue === null) ? (
                <ButtonContent
                  selected={selectedValue}
                  options={options}
                  onChange={onChange}
                  allowClear={allowClear}
                  multiple={allowMultiple}
                  linkBase={linkBase}
                />
              ) : null}

              <li className="relative w-6 flex-grow">
                <Combobox.Input
                  className="my-[2px] w-full rounded-md border-0 text-sm focus-within:outline-none focus-within:ring-0 focus:outline-none focus:ring-0 focus:ring-transparent disabled:cursor-not-allowed disabled:bg-gray-100"
                  placeholder={
                    selectedValue
                      ? ""
                      : hasNullOption && selectedValue === null
                      ? ""
                      : "Please Select"
                  }
                  onChange={(event) => {
                    if (selectedValue) {
                      clear();
                      setQuery(event.target.value);
                    } else {
                      setQuery(event.target.value);
                    }
                  }}
                  value={query === selectedValue ? "" : query}
                  onKeyDown={(event: any) => {
                    if (event.key === "Enter") {
                      setQuery("");
                    }

                    if (
                      event.key === "Backspace" &&
                      query === "" &&
                      selectedValue
                    ) {
                      clear();
                    }
                  }}
                  onBlur={() => {
                    onBlur(field.name);
                  }}
                />

                <Combobox.Button className="bg-transparent absolute inset-y-0 right-0 flex items-center pr-1">
                  <ChevronUpDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </Combobox.Button>
              </li>
            </ul>
          </div>

          {filteredOptions.length > 0 && (
            <Combobox.Options className="absolute z-[9999] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredOptions.map((option) => (
                <Combobox.Option
                  key={option.value}
                  value={option.value}
                  className={({ active }) =>
                    classNames(
                      "relative cursor-default select-none py-2 px-4",
                      active ? "bg-martEye-400 text-white" : "text-gray-900"
                    )
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <div className="flex items-center">
                        <span
                          className={classNames(
                            selected ? "font-semibold" : "font-normal",
                            "block truncate"
                          )}
                        >
                          {option.label}
                        </span>
                      </div>
                      {selected ? (
                        <span
                          className={classNames(
                            active ? "text-white" : "text-martEye-400",
                            "absolute inset-y-0 right-0 flex items-center pr-4"
                          )}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          )}
        </div>
      </Combobox>

      {meta.error && (
        <div className="text-sm text-danger-500 leading-tight mt-1 capitalize">
          {meta.error}
        </div>
      )}
    </div>
  );
};

export default ComboBoxField;

const ButtonContent = ({
  selected,
  options,
  onChange,
  allowClear,
  multiple,
  linkBase,
}: any) => {
  const hasNullOption = options.some(
    (option: { value: string | null; label: string }) => option.value === null
  );

  if (selected || (hasNullOption && selected === null)) {
    if (multiple) {
      return (
        <div className="inline-flex gap-x-2">
          {selected.map((key: string) => {
            let selectedOption = options.find(
              (option: { value: string | null; label: string }) =>
                option.value === key
            );

            return (
              <span
                className={classNames(
                  "inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium uppercase text-martEye-700 ring-1 ring-inset ring-gray-100",
                  linkBase &&
                    "cursor-pointer hover:bg-martEye-300 hover:ring-gray-martEye-300"
                )}
                key={key}
              >
                {linkBase ? (
                  <Link href={`/${linkBase}/${key}`} target="_blank">
                    {selectedOption?.label}
                  </Link>
                ) : (
                  selectedOption?.label
                )}

                {allowClear && (
                  <span
                    className="cursor-pointer"
                    onClick={(e) => {
                      // Only remove the selected value
                      let newSelected: string[] =
                        selected.filter((value: string) => value !== key) || [];
                      onChange(newSelected);
                      e.stopPropagation();
                    }}
                  >
                    <XMarkIcon className="ml-0.5 h-5 w-5 text-gray-500" />
                  </span>
                )}
              </span>
            );
          })}
        </div>
      );
    }
    let selectedLabel = options.find(
      (option: { label: string; value: string }) => option.value === selected
    )?.label;

    let selectedValue = options.find(
      (option: { label: string; value: string }) => option.value === selected
    )?.value;

    return (
      <span
        className={classNames(
          "inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium uppercase text-martEye-700 ring-1 ring-inset ring-gray-100",
          linkBase &&
            "cursor-pointer hover:bg-martEye-300 hover:ring-gray-martEye-300"
        )}
      >
        {linkBase ? (
          <Link href={`/${linkBase}/${selectedValue}`} target="_blank">
            {selectedLabel}
          </Link>
        ) : (
          selectedLabel
        )}

        {allowClear && (
          <span
            className="cursor-pointer"
            onClick={(e) => {
              onChange(null);
              e.stopPropagation();
            }}
          >
            <XMarkIcon className="ml-0.5 h-5 w-5 text-gray-500" />
          </span>
        )}
      </span>
    );
  } else {
    return <span>Please select</span>;
  }
};

// Helper function to get the value of a field
function formatLabel(str: string) {
  // Add space between numbers and uppercase every word
  const formattedStr = str
    .replace(/(\d+)([a-zA-Z])/g, "$1 $2")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([a-zA-Z])(\d+)/g, "$1 $2")
    .replace(/\b\w/g, (match) => match.toUpperCase());

  // Return the formatted string
  return formattedStr;
}
